// =============================================================================
// Optim TURPE v2 — RELECTURE des études après une panne de réponse (DEV-1583).
//
// Intention : « une étude qui aboutit est servie au client, quelle que soit la
// durée du calcul ; une réponse d'erreur destinée au client est émise par
// l'application, jamais laissée à l'infrastructure. »
//
// Le fait (03/09/2026, prod) : clic à 07:31:40,37 ; le moteur courtage écrit ses
// deux `AnalyseV2` en SUCCESS à 07:32:11,47 (31,05 s) ; mais les routeurs Heroku
// coupent à 30 s (H12 côté courtage, coupure de la réponse côté api-metier). Le
// POST du front n'a donc jamais reçu la réponse d'un calcul qui avait RÉUSSI, et
// l'écran servait une erreur pour deux études existantes (mode 2 de DEV-1529).
//
// Ce module relit la liste des études après une panne : ce qui a abouti est
// affiché, ce qui n'a pas abouti est dit sans cause inventée (DEV-1582).
// =============================================================================

import { ServiceError } from "@core/exceptions"
import type { OptimTurpeV2Etude } from "@modules/courtage/types/optim-turpe-v2"

/** Cadence de relecture (une lecture toutes les 5 s). */
export const RELECTURE_INTERVALLE_MS = 5_000

/**
 * Fenêtre totale de relecture (90 s). Calibrée sur le fait mesuré : le calcul
 * observé a duré 31 s, soit à peine plus que la coupure du routeur ; 90 s couvre
 * largement le dépassement sans immobiliser l'écran indéfiniment.
 */
export const RELECTURE_FENETRE_MS = 90_000

/**
 * Tolérance d'horloge entre le navigateur (qui date le clic) et le serveur (qui
 * date `created_at`). Sans elle, un navigateur en avance de quelques secondes ne
 * reconnaîtrait jamais ses propres études.
 *
 * ⚠️ Revue adversariale du 03/09 (blocant 1) : cette tolérance ouvre une fenêtre
 * `]clic − 60 s, clic[` dans laquelle une étude d'un run PRÉCÉDENT sur le même
 * compteur satisfait les trois critères (`_id_source`, `allow_cmdps`, date) et
 * serait servie comme le résultat du nouveau clic, avec les anciens paramètres.
 * Scénario : succès à T₀, « Affiner l'étude » à T₀+30 s, POST coupé, le nouveau
 * calcul encore `PENDING` — l'ancien est alors le seul candidat terminal.
 *
 * La tolérance n'est donc utilisée QUE si le relevé des études d'avant-clic a
 * abouti (`releveAvantClicFiable`), auquel cas les `_id` déjà existants sont
 * exclus nommément et la fenêtre ne peut plus rien ramener d'ancien. Sans ce
 * relevé, le plancher est STRICT (aucune tolérance) : mieux vaut afficher
 * « le calcul continue » que servir le résultat d'un autre clic.
 */
export const RELECTURE_TOLERANCE_HORLOGE_MS = 60_000

/**
 * Libellé client d'une durée de fenêtre, DÉRIVÉ de la constante (revue 03/09
 * seconde passe). Écrire « 90 secondes » en dur dans un message rendait le texte
 * servi au client faux au premier changement de `RELECTURE_FENETRE_MS` : un
 * message est une affirmation, il doit suivre ce qu'il affirme.
 */
export function libelleFenetre(ms: number): string {
    return `${Math.round(ms / 1_000)} secondes`
}

/**
 * Immobilité au delà de laquelle une étude non terminée est dite ABANDONNÉE
 * plutôt que « en cours » (DEV-1621).
 *
 * Calibré sur la mesure : les 26 `AnalyseV2` du bâtiment témoin
 * (692ffeac0f28d5945f3d9832, relues le 2026-09-04T12:09Z) qui ont abouti l'ont
 * fait en 9 s à 31 s. 15 minutes valent une trentaine de fois le plus long calcul
 * observé : au delà, un `PENDING` n'attend plus rien. Le moteur n'a d'ailleurs
 * aucune reprise des analyses perdues (DEV-1529), donc rien ne le reprendra.
 */
export const SEUIL_ETUDE_ABANDONNEE_MS = 15 * 60 * 1000

/**
 * État RÉEL d'une étude de ce clic qui n'a pas abouti (DEV-1621).
 *
 * Le fait : sur le bâtiment témoin, le clic de 2026-09-04T11:16:39Z a créé deux
 * études ; l'une est passée `SUCCESS` à 11:16:51, l'autre était encore `PENDING`
 * avec `updated_at == created_at` à 12:09Z, soit près d'une heure plus tard. Le
 * clic de 10:59:11Z a créé deux études restées `PENDING` toutes les deux. Deux
 * autres études (25/06) sont en `FAILURE` avec un `error.message` VIDE. Dans les
 * trois cas l'écran servait « Le calcul continue en arrière-plan », une
 * affirmation que rien ne soutenait.
 *
 * On ne remplace pas une affirmation fausse par une autre : chaque état ne dit
 * que ce qui a été constaté, jamais ce qui va se passer.
 */
export type EtatNonTerminal =
    | {
          /** Ouverte, pas encore terminée, rien ne permet de la dire perdue. */
          etat: "en_cours"
          ouverteA: number | null
      }
    | {
          /** Le moteur a marqué `FAILURE` sans renvoyer la moindre cause. */
          etat: "echec_muet"
          ouverteA: number | null
      }
    | {
          /**
           * Immobile au delà du seuil, ou laissée en attente alors qu'un autre
           * scénario du MÊME clic a abouti.
           */
          etat: "abandonnee"
          ouverteA: number | null
          immobileDepuisMs: number | null
          /** Un autre scénario de ce clic a abouti. */
          jumeauAbouti: boolean
      }

/** Heure de Paris « à 13:16 », ou chaîne vide si l'horodatage manque. */
function heureDeParis(ms: number | null): string {
    if (ms === null) return ""
    return new Intl.DateTimeFormat("fr-FR", {
        timeZone: "Europe/Paris",
        hour: "2-digit",
        minute: "2-digit",
    }).format(new Date(ms))
}

/**
 * Message servi quand la relecture a VU l'étude de ce clic sans la voir aboutir.
 *
 * Chaque phrase est un constat daté : l'étude a été ouverte, elle n'a pas abouti
 * dans la fenêtre, aucune cause n'est connue. On ne dit plus « le calcul
 * continue », que rien ne prouve. Le renvoi vers l'aide est rendu par la page
 * sous le message (DEV-2311), pas écrit ici : il dépend de la session.
 * @param etat - État réel constaté sur les documents relus.
 * @returns Texte destiné au client.
 */
export function messageEtudeNonTerminale(etat: EtatNonTerminal): string {
    const a = heureDeParis(etat.ouverteA)
    const ouverte = a ? `L'étude ouverte à ${a}` : "L'étude ouverte"
    if (etat.etat === "echec_muet") {
        return `Le calcul a échoué et aucune cause n'est connue. ${ouverte} est marquée en échec sans explication. Relancez l'étude.`
    }
    if (etat.etat === "abandonnee") {
        const constat = etat.jumeauAbouti
            ? `${ouverte} est restée en attente alors que l'autre scénario du même calcul a abouti.`
            : `${ouverte} n'a plus bougé depuis.`
        return `Le calcul n'a pas abouti. ${constat} Aucune cause n'est connue. Relancez l'étude.`
    }
    return `${ouverte} n'a pas abouti en ${libelleFenetre(RELECTURE_FENETRE_MS)}, sans résultat ni cause connue. Rechargez la page dans une minute ; si rien ne change, relancez l'étude.`
}

/**
 * Complément servi quand la relecture n'a RIEN vu (revue 03/09, blocant 2).
 *
 * Le problème : `resolveServiceErrorMessage` consulte la description servie par
 * l'API EN PREMIER, et api-metier sert sur ce même chemin une description qui
 * parle du délai de réponse dépassé. Sans ce complément, le cas « j'ai interrogé
 * la liste 18 fois sur 90 s et je n'ai vu aucune étude » afficherait un texte
 * écrit AVANT cette vérification, donc moins informé qu'elle : le cas le moins
 * renseigné deviendrait le plus affirmatif.
 *
 * On ne SUPPRIME pas la cause servie (règle de DEV-1582 : afficher ce que le
 * serveur a dit), on lui AJOUTE le fait vérifié côté écran. Les deux phrases
 * disent chacune ce qu'elles savent, et la seconde est datée par sa mesure.
 */
export const MESSAGE_AUCUNE_ETUDE_VUE = `Vérification faite pendant ${libelleFenetre(RELECTURE_FENETRE_MS)} : aucune étude n'est arrivée pour ce compteur.`

/**
 * Message servi quand AUCUNE lecture n'a abouti (revue 03/09 seconde passe, défaut 1).
 *
 * La panne qui coupe le POST (H12 du routeur, 503) traverse la même chaîne
 * api-metier puis courtage que le `GET /analyses` : quand elle dure, les 18
 * lectures échouent avec lui. Servir alors « nous avons vérifié pendant 90
 * secondes : aucune étude n'est arrivée » affirmerait une vérification qui n'a
 * PAS eu lieu, exactement la faute du blocant 2 déplacée sur l'autre branche.
 *
 * « Rien vu » et « pas pu voir » sont deux états distincts : le second ne dit rien
 * de l'étude, seulement de notre capacité à la chercher.
 */
export const MESSAGE_VERIFICATION_IMPOSSIBLE =
    "L'arrivée d'une étude n'a pas pu être vérifiée : la liste des études est elle aussi injoignable."

/**
 * Une panne de NOTRE côté, au sens où l'écran doit d'abord aller voir si le
 * travail a quand même abouti.
 *
 * Le critère est EXACTEMENT celui de `resolveServiceErrorMessage` après la revue
 * du 03/09 sur DEV-1582 : **le statut HTTP, jamais le code**. Statut absent (erreur
 * réseau ou réponse bloquée par CORS, le cas H12) ou ≥ 500 (panne serveur déclarée,
 * dont le 503 du routeur) → panne. Les deux fonctions doivent rester alignées :
 * si l'une relit et l'autre non, l'écran afficherait le repli de panne sans être
 * allé voir, ou irait voir pour une erreur métier déjà expliquée.
 *
 * Un 4xx n'est JAMAIS une panne, même sans cause exploitable : rien à relire, le
 * moteur a répondu et a refusé.
 *
 * Une erreur qui n'est pas une `ServiceError` (une annulation `axios.isCancel`,
 * relancée telle quelle par `handleServiceCall`) n'est pas une panne non plus :
 * on ne relit pas ce que l'utilisateur vient d'annuler.
 */
export function estPanneDeReponse(error: unknown): boolean {
    if (!(error instanceof ServiceError)) return false
    const status = error.status
    return status === null || status === undefined || status >= 500
}

/** Source d'une étude telle que persistée (`inputs.source._id_source`). */
function getSourceId(etude: OptimTurpeV2Etude): string {
    return String(etude.inputs?.source?._id_source ?? "")
}

/**
 * Lit un horodatage rendu par le back (ms epoch), ou `null` s'il est absent ou
 * illisible.
 *
 * ⚠️ Deux pièges, tous deux vérifiés sur le chemin RÉEL de cette relecture
 * (`GET /optimturpe/analyse` de `homeys_analyse` renvoie les documents Mongo
 * bruts, encodés par `jsonable_encoder`) :
 *
 * 1. **Le fuseau est absent.** `homeys_mongo.ApiMongo` ouvre `pymongo.MongoClient`
 *    SANS `tz_aware=True` : les datetimes relus de Mongo sont naïfs, et
 *    `isoformat()` rend « 2026-09-03T07:32:11.474000 », sans `Z`. `Date.parse`
 *    interpréterait alors une heure LOCALE (2 h d'écart à Paris en septembre) et
 *    l'étude paraîtrait ANTÉRIEURE au clic : la relecture ne reconnaîtrait jamais
 *    ses propres études et le correctif serait silencieusement inerte. Ces
 *    horodatages sont UTC côté serveur : on le dit explicitement.
 * 2. **La fraction de seconde fait 6 chiffres** (microsecondes Python), alors que
 *    le format ISO simplifié d'ECMAScript en spécifie 3. On la ramène à 3.
 */
export function parseHorodatageServeur(brut: unknown): number | null {
    if (typeof brut !== "string") return null
    const texte = brut.trim()
    if (texte === "") return null
    const sansMicrosecondes = texte.replace(/(\.\d{3})\d+/, "$1")
    const avecFuseau = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(sansMicrosecondes)
        ? sansMicrosecondes
        : `${sansMicrosecondes}Z`
    const ms = Date.parse(avecFuseau)
    return Number.isNaN(ms) ? null : ms
}

/** Instant de création (ms epoch), ou `null` si la date est absente/illisible. */
function getCreatedAtMs(etude: OptimTurpeV2Etude): number | null {
    return parseHorodatageServeur(etude.created_at)
}

/** Échec NOMMÉ : le moteur a persisté une cause (`error.code` ou `error.message`). */
export function estEchecNomme(etude: OptimTurpeV2Etude): boolean {
    if (etude.status !== "FAILURE") return false
    const code = (etude.error?.code ?? "").trim()
    const message = (etude.error?.message ?? "").trim()
    return code !== "" || message !== ""
}

/**
 * Étude TERMINALE au sens du client : soit elle a abouti (`SUCCESS`), soit elle a
 * échoué EN NOMMANT sa cause. Un `FAILURE` muet et un `PENDING` ne sont pas
 * terminaux — on continue de relire plutôt que d'afficher un résultat inexplicable.
 */
export function estTerminale(etude: OptimTurpeV2Etude): boolean {
    return etude.status === "SUCCESS" || estEchecNomme(etude)
}

/** Le scénario CMDPS demandé (`undefined` = n'importe lequel, run simple). */
function correspondAuScenario(
    etude: OptimTurpeV2Etude,
    allowCmdps: boolean | undefined
): boolean {
    if (allowCmdps === undefined) return true
    return etude.inputs?.optimisation?.allow_cmdps === allowCmdps
}

/** Options de sélection/relecture (partagées par les deux fonctions publiques). */
export type RelectureOptions = {
    /** `_id_source` (Mongo) de la source du clic. */
    sourceId: string
    /** Instant du clic (ms epoch, horloge du navigateur). */
    depuis: number
    /**
     * Scénarios attendus, dans l'ordre d'affichage. `runArbitrage` en attend deux
     * (`[false, true]` = sans / avec CMDPS) ; `runBySource` un seul (`[undefined]`
     * ou la valeur explicitement demandée).
     */
    scenarios: Array<boolean | undefined>
    /**
     * `_id` des études qui EXISTAIENT DÉJÀ avant le clic (relevé à l'instant du
     * clic + celles que l'écran affichait). Elles ne peuvent pas être le résultat
     * de ce clic : exclusion nominative, indépendante de toute horloge.
     */
    idsAvantClic?: ReadonlySet<string>
    /**
     * Le relevé d'avant-clic a-t-il abouti ? Faux (défaut) = on ne sait pas ce qui
     * existait, donc plancher STRICT sans tolérance d'horloge.
     */
    releveAvantClicFiable?: boolean
    intervalleMs?: number
    fenetreMs?: number
    toleranceMs?: number
}

/**
 * Études de CE clic : jamais une étude qui existait déjà, jamais une étude d'une
 * autre source, jamais une étude antérieure au plancher. Les plus récentes d'abord.
 *
 * Deux gardes INDÉPENDANTES contre « servir une ancienne étude comme la nouvelle »
 * (revue 03/09, blocant 1) : l'exclusion nominative des `_id` d'avant-clic, qui ne
 * dépend d'aucune horloge, et le plancher temporel, strict quand le relevé
 * d'avant-clic a échoué. L'une couvre ce que l'autre laisse passer.
 */
function candidatesDuClic(
    etudes: OptimTurpeV2Etude[],
    opts: RelectureOptions
): OptimTurpeV2Etude[] {
    const tolerance = opts.releveAvantClicFiable
        ? (opts.toleranceMs ?? RELECTURE_TOLERANCE_HORLOGE_MS)
        : 0
    const plancher = opts.depuis - tolerance
    const dejaLa = opts.idsAvantClic
    return etudes
        .filter((e) => !(dejaLa && e._id && dejaLa.has(String(e._id))))
        .filter((e) => getSourceId(e) === opts.sourceId)
        .map((e) => ({ etude: e, ms: getCreatedAtMs(e) }))
        .filter((x): x is { etude: OptimTurpeV2Etude; ms: number } =>
            // Date illisible = on ne peut PAS affirmer que l'étude est celle du clic.
            x.ms !== null ? x.ms >= plancher : false
        )
        .sort((a, b) => b.ms - a.ms)
        .map((x) => x.etude)
}

/**
 * Sélectionne, dans une liste d'études, celles produites par CE clic — une par
 * scénario attendu, la plus récente d'abord. Renvoie `null` tant que les
 * scénarios ne sont pas TOUS pourvus d'une étude terminale : un demi-arbitrage
 * n'est pas un résultat.
 */
export function selectionnerEtudesRelues(
    etudes: OptimTurpeV2Etude[],
    opts: RelectureOptions
): OptimTurpeV2Etude[] | null {
    const candidates = candidatesDuClic(etudes, opts).filter(estTerminale)
    const retenues: OptimTurpeV2Etude[] = []
    const prises = new Set<OptimTurpeV2Etude>()
    for (const scenario of opts.scenarios) {
        const trouvee = candidates.find(
            (e) => !prises.has(e) && correspondAuScenario(e, scenario)
        )
        if (!trouvee) return null
        prises.add(trouvee)
        retenues.push(trouvee)
    }
    return retenues
}

/** Y a-t-il au moins une étude de ce clic, terminale ou non ? (fait constaté). */
export function aVuUneEtudeDuClic(
    etudes: OptimTurpeV2Etude[],
    opts: RelectureOptions
): boolean {
    return candidatesDuClic(etudes, opts).length > 0
}

/**
 * Dernier mouvement du document (ms epoch) : `updated_at` s'il est lisible, sinon
 * `created_at`. Le moteur n'écrit `updated_at` qu'à la fin du calcul, donc
 * `updated_at == created_at` ne dit rien de l'avancement : c'est bien l'ÂGE de ce
 * dernier mouvement qui sépare « pas encore fini » de « ne bougera plus ».
 */
function getDernierMouvementMs(etude: OptimTurpeV2Etude): number | null {
    return (
        parseHorodatageServeur(etude.updated_at) ??
        parseHorodatageServeur(etude.created_at)
    )
}

/**
 * Classe l'étude non terminée de ce clic (DEV-1621) : échec muet, abandon
 * constaté, ou attente dont on ne peut rien dire de plus.
 *
 * @param etudes - Liste relue (dernière lecture aboutie).
 * @param opts - Options de sélection du clic.
 * @param maintenant - Instant du verdict (ms epoch).
 * @param seuilAbandonMs - Immobilité au delà de laquelle l'étude est dite abandonnée.
 * @returns L'état constaté, ou `null` si aucune étude non terminée de ce clic.
 */
export function classerEtudeNonTerminale(
    etudes: OptimTurpeV2Etude[],
    opts: RelectureOptions,
    maintenant: number,
    seuilAbandonMs: number = SEUIL_ETUDE_ABANDONNEE_MS
): EtatNonTerminal | null {
    const candidates = candidatesDuClic(etudes, opts)
    const nonTerminales = candidates.filter((e) => !estTerminale(e))
    if (nonTerminales.length === 0) return null

    // Un `FAILURE` sans cause est déjà un échec : le dire vaut mieux que
    // prétendre que le calcul se poursuit. C'est le cas réel des deux études du
    // 25/06 sur le bâtiment témoin (`error.message` vide).
    const muette = nonTerminales.find((e) => e.status === "FAILURE")
    if (muette) {
        return {
            etat: "echec_muet",
            ouverteA: parseHorodatageServeur(muette.created_at),
        }
    }

    const attente = nonTerminales[0]
    const ouverteA = parseHorodatageServeur(attente.created_at)
    const mouvement = getDernierMouvementMs(attente)
    const immobileDepuisMs = mouvement === null ? null : maintenant - mouvement
    const jumeauAbouti = candidates.some(estTerminale)

    if (
        jumeauAbouti ||
        (immobileDepuisMs !== null && immobileDepuisMs > seuilAbandonMs)
    ) {
        return { etat: "abandonnee", ouverteA, immobileDepuisMs, jumeauAbouti }
    }
    return { etat: "en_cours", ouverteA }
}

/**
 * Issue d'une relecture — trois états, jamais une cause inventée :
 * - `etudes` : les études de ce clic sont là, terminales → à afficher comme si le
 *   POST avait répondu ;
 * - `en_cours` : l'étude de ce clic EXISTE mais n'est pas terminée → le calcul
 *   continue réellement en arrière-plan (fait constaté) ;
 * - `introuvable` : aucune étude de ce clic n'a été vue → on ne sait pas, on garde
 *   le repli de panne de DEV-1582 et on n'affirme rien.
 *
 * `lectureAboutie` sépare deux `introuvable` que le premier jet confondait (revue
 * 03/09 seconde passe, défaut 1) : « la liste a répondu et elle ne contient rien »
 * est une vérification ; « aucune lecture n'a abouti » n'en est pas une, et ne
 * peut pas être servie au client comme telle.
 */
export type RelectureIssue =
    | { issue: "etudes"; etudes: OptimTurpeV2Etude[] }
    | { issue: "en_cours"; etat: EtatNonTerminal }
    | { issue: "introuvable"; lectureAboutie: boolean }

/** Dépendances injectées (horloge, attente, lecture) — rend la boucle testable. */
export type RelectureDeps = {
    lister: () => Promise<OptimTurpeV2Etude[]>
    attendre: (ms: number) => Promise<void>
    maintenant: () => number
    /** Vrai si un nouveau run a pris la main : on abandonne sans toucher l'état. */
    estAnnulee?: () => boolean
}

/**
 * Relit `GET /v5/optimturpe/v2/analyses` à cadence fixe pendant la fenêtre, et
 * s'arrête dès que les études de ce clic sont terminales.
 *
 * Une lecture qui échoue ne masque PAS la panne d'origine : on l'ignore et on
 * réessaie au tick suivant (au pire, on conclut `introuvable` et l'écran affiche
 * le repli de panne). Mais on RETIENT si une lecture au moins a rendu une liste
 * (`lectureAboutie`) : la panne qui coupe le POST traverse la même chaîne que le
 * `GET /analyses`, donc « rien vu » et « pas pu lire » arrivent tous deux ici, et
 * seul le premier autorise à dire au client que l'on a vérifié.
 *
 * Limite connue et assumée : une étude bloquée en `PENDING` pour de bon (worker
 * tué en cours de calcul, mode 1 « H13 OOM » de DEV-1529) est indiscernable d'un
 * calcul encore en cours. Elle est donc rendue `en_cours`. Le remède de fond est
 * le run asynchrone (étape 2 de DEV-1583) et la reprise des analyses perdues
 * (DEV-1529), pas un message d'écran.
 */
export async function relireEtudesApresPanne(
    deps: RelectureDeps,
    opts: RelectureOptions
): Promise<RelectureIssue> {
    const intervalleMs = opts.intervalleMs ?? RELECTURE_INTERVALLE_MS
    const fenetreMs = opts.fenetreMs ?? RELECTURE_FENETRE_MS
    const fin = deps.maintenant() + fenetreMs
    let vue = false
    // Au moins une lecture a-t-elle RENDU une liste ? Sans ce drapeau, une fenêtre
    // entière de lectures échouées se conclut comme une fenêtre entière de listes
    // vides, et l'écran affirme une vérification qui n'a pas eu lieu.
    let lectureAboutie = false
    // Dernière liste RELUE : c'est sur elle que se lit l'état réel de l'étude au
    // moment du verdict (DEV-1621), pas sur un booléen.
    let derniereListe: OptimTurpeV2Etude[] = []
    for (;;) {
        await deps.attendre(intervalleMs)
        if (deps.estAnnulee?.()) return { issue: "introuvable", lectureAboutie }
        let etudes: OptimTurpeV2Etude[] = []
        try {
            etudes = await deps.lister()
            lectureAboutie = true
            derniereListe = etudes
        } catch {
            etudes = []
        }
        if (deps.estAnnulee?.()) return { issue: "introuvable", lectureAboutie }
        const retenues = selectionnerEtudesRelues(etudes, opts)
        if (retenues) return { issue: "etudes", etudes: retenues }
        vue = vue || aVuUneEtudeDuClic(etudes, opts)
        if (deps.maintenant() >= fin) {
            if (!vue) return { issue: "introuvable", lectureAboutie }
            const etat = classerEtudeNonTerminale(
                derniereListe,
                opts,
                deps.maintenant()
            )
            // `vue` a pu être posé par une lecture antérieure dont la dernière
            // liste ne porte plus la trace : on ne fabrique alors aucun détail.
            return {
                issue: "en_cours",
                etat: etat ?? { etat: "en_cours", ouverteA: null },
            }
        }
    }
}

// =============================================================================
// SUIVI d'un calcul MIS EN FILE (DEV-1529) — le chemin NOMINAL, pas la panne.
//
// Ce que la relecture ci-dessus traite : une réponse coupée (H12, 503), donc une
// panne. Ce que ce bloc traite est différent et arrive désormais SANS panne : la
// façade répond 202 « le calcul est en file », avec l'identifiant de l'étude.
// L'écran doit alors dire qu'un calcul est en cours et relire l'étude jusqu'à son
// état terminal, au lieu d'afficher le document `PENDING` comme un résultat.
//
// Le fait du 07/09 : le 202 traversait `axios` (200-299 vaut succès par défaut),
// remontait en 200, et la page affichait une étude vide. Le spinner s'éteignait
// en une seconde. Aucune erreur nulle part.
// =============================================================================

/** Cadence de relecture d'un calcul en file (une lecture toutes les 5 s). */
export const SUIVI_INTERVALLE_MS = 5_000

/**
 * Plafond du suivi (5 minutes).
 *
 * Calé d'abord sur la mesure versée à DEV-1529 le 07/09 (p95 22,0 s), puis
 * relevé par le bêta-test du 02/10 (DEV-2311) : quand plusieurs compteurs sont
 * lancés ensemble, la file du worker (deux calculs de front) porte une étude
 * jusqu'à 163 s, et l'écran abandonnait à 120 s une étude qui aboutissait. Ce
 * plafond ne dit RIEN du calcul, qui vit sur le worker et poursuit sa route :
 * il borne seulement ce que l'écran attend.
 */
export const SUIVI_PLAFOND_MS = 300_000

/** Ce que l'écran affiche pendant que le calcul est en file (fait constaté). */
export const MESSAGE_CALCUL_EN_COURS =
    "Calcul en cours. L'étude analyse la courbe de charge sur la période demandée ; l'écran se met à jour dès qu'elle aboutit."

/**
 * Message servi au plafond (DEV-2311).
 *
 * Il dit ce qui se passe : l'étude est en file, elle se poursuit sans la page,
 * et elle s'affichera au retour, ce qui est vrai depuis que l'ouverture reprend
 * le suivi d'un lancement en file au lieu d'en relancer un (F3). Il ne promet
 * ni délai ni aboutissement : au retour s'affiche le résultat, ou le refus
 * nommé.
 */
export const MESSAGE_CALCUL_PLUS_LONG =
    "Votre étude est en attente de calcul et se poursuit sans vous. Vous pouvez quitter cette page : elle s'affichera ici à votre retour."

/**
 * Message servi quand le moteur prend le calcul en charge SANS rendre
 * d'identifiant d'étude (DEV-2412) : l'écran n'a rien à suivre, et rien ne
 * garantit que l'ouverture retrouvera l'étude. Il ne promet donc pas
 * l'affichage au retour, à la différence de `MESSAGE_CALCUL_PLUS_LONG`.
 */
export const MESSAGE_CALCUL_SANS_SUIVI =
    "Votre étude est en attente de calcul, mais cet écran ne peut pas en suivre l'avancement. Rechargez la page dans quelques minutes pour savoir si elle a abouti."

/**
 * Issue d'un suivi — quatre états, aucun inventé :
 * - `terminee` : l'étude a abouti, elle est servie ;
 * - `echec_nomme` : le moteur a écrit un `FAILURE` AVEC sa cause, on l'affiche ;
 * - `echec_muet` : `FAILURE` sans la moindre cause, dit comme tel (DEV-1621) ;
 * - `plafond` : rien de terminal dans la fenêtre, on ne conclut rien ;
 * - `abandonne` : un autre run a pris la main, on n'écrit rien du tout.
 */
export type SuiviIssue =
    | { issue: "terminee"; etude: OptimTurpeV2Etude }
    | { issue: "echec_nomme"; cause: string }
    | { issue: "echec_muet"; etat: EtatNonTerminal }
    | { issue: "plafond" }
    | { issue: "abandonne" }

/** Dépendances injectées (lecture, horloge, attente) — rend la boucle testable. */
export type SuiviDeps = {
    /** Relit l'étude par son identifiant (`GET /analyse/:id` via la façade). */
    lire: () => Promise<OptimTurpeV2Etude | null | undefined>
    attendre: (ms: number) => Promise<void>
    maintenant: () => number
    /** Vrai si un nouveau run a pris la main : on abandonne sans toucher l'état. */
    estAnnulee?: () => boolean
}

export type SuiviOptions = {
    intervalleMs?: number
    plafondMs?: number
}

/** Cause NOMMÉE d'un échec, ou chaîne vide si le moteur n'en a écrit aucune. */
function causeNommee(etude: OptimTurpeV2Etude): string {
    return (
        (etude.error?.message ?? "").trim() || (etude.error?.code ?? "").trim()
    )
}

/**
 * Relit une étude mise en file, à cadence fixe, jusqu'à son état terminal ou
 * jusqu'au plafond.
 *
 * Une lecture qui échoue n'interrompt PAS le suivi : le calcul, lui, continue sur
 * le worker, et une coupure réseau passagère ne doit pas se transformer en verdict.
 * On réessaie au tick suivant ; au pire on conclut `plafond`, qui n'affirme rien.
 *
 * La PREMIÈRE lecture a lieu après un intervalle, jamais immédiatement : la
 * façade vient de rendre le document `PENDING`, le relire dans la milliseconde ne
 * peut rien apprendre et ne ferait qu'une requête de plus.
 */
export async function suivreCalculEnCours(
    deps: SuiviDeps,
    options: SuiviOptions = {}
): Promise<SuiviIssue> {
    const intervalleMs = options.intervalleMs ?? SUIVI_INTERVALLE_MS
    const plafondMs = options.plafondMs ?? SUIVI_PLAFOND_MS
    const fin = deps.maintenant() + plafondMs
    for (;;) {
        await deps.attendre(intervalleMs)
        if (deps.estAnnulee?.()) return { issue: "abandonne" }
        let etude: OptimTurpeV2Etude | null | undefined
        try {
            etude = await deps.lire()
        } catch {
            etude = null
        }
        if (deps.estAnnulee?.()) return { issue: "abandonne" }
        if (etude) {
            if (etude.status === "SUCCESS") return { issue: "terminee", etude }
            if (etude.status === "FAILURE") {
                const cause = causeNommee(etude)
                if (cause) return { issue: "echec_nomme", cause }
                // Un `FAILURE` sans cause ne changera plus : continuer de le
                // relire ferait attendre le client pour rien (DEV-1621).
                return {
                    issue: "echec_muet",
                    etat: {
                        etat: "echec_muet",
                        ouverteA: parseHorodatageServeur(etude.created_at),
                    },
                }
            }
        }
        if (deps.maintenant() >= fin) return { issue: "plafond" }
    }
}
